// frontend/src/services/api.js

import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "/api";

// ============================================================
// AXIOS INTERCEPTORS FOR DEBUGGING
// ============================================================

// Request Interceptor - Log all outgoing requests
axios.interceptors.request.use(
  (config) => {
    console.log(
      `🚀 API Request: ${config.method?.toUpperCase() || "GET"} ${config.url}`,
    );
    console.log("📦 Headers:", config.headers);
    if (config.data) {
      // Don't log file uploads (FormData)
      if (config.data instanceof FormData) {
        console.log("📝 Data: [FormData]");
      } else {
        console.log("📝 Data:", config.data);
      }
    }
    if (config.params) {
      console.log("🔍 Params:", config.params);
    }
    return config;
  },
  (error) => {
    console.error("❌ Request Error:", error);
    return Promise.reject(error);
  },
);

// Response Interceptor - Log all responses
axios.interceptors.response.use(
  (response) => {
    console.log(
      `✅ API Response: ${response.config.method?.toUpperCase() || "GET"} ${response.config.url}`,
    );
    console.log("📦 Status:", response.status);
    if (response.data) {
      // Don't log large responses
      if (typeof response.data === "object" && response.data.length > 100) {
        console.log(`📝 Data: [${response.data.length} items]`);
      } else {
        console.log("📝 Data:", response.data);
      }
    }
    return response;
  },
  (error) => {
    console.error("❌ Response Error:");
    console.error("📦 Status:", error.response?.status);
    console.error("📝 Data:", error.response?.data);
    console.error("📝 Message:", error.message);
    console.error("📝 URL:", error.config?.url);
    return Promise.reject(error);
  },
);

// ============================================================
// API METHODS
// ============================================================

const api = {
  // ============================================================
  // AUTH
  // ============================================================
  login: async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, {
      email,
      password,
    });
    return response.data;
  },

  register: async (userData) => {
    const response = await axios.post(`${API_URL}/auth/register`, userData);
    return response.data;
  },
  verifyRegistration: async (email, code) => {
    const response = await axios.post(`${API_URL}/auth/register/verify`, {
      email,
      code,
    });
    return response.data;
  },

  resendRegistrationCode: async (email) => {
    const response = await axios.post(`${API_URL}/auth/register/resend`, {
      email,
    });
    return response.data;
  },

  logout: async () => {
    try {
      await axios.post(`${API_URL}/auth/logout`);
    } catch (error) {
      console.error("Logout error:", error);
    }
  },

  getProfile: async () => {
  const response = await axios.get(`${API_URL}/profile`);
  return response.data;
},

  // ============================================================
  // LEADS
  // ============================================================
  getSalesReps: async () => {
    const response = await axios.get(`${API_URL}/leads/sales-reps`);
    return response.data;
  },

  getLeads: async (filters = {}) => {
    // Add cache buster to prevent 304 responses
    const params = {
      ...filters,
      _t: Date.now(),
    };
    const response = await axios.get(`${API_URL}/leads`, { params });
    return response.data;
  },

  getLead: async (id) => {
    const response = await axios.get(`${API_URL}/leads/${id}`);
    return response.data;
  },

  createLead: async (data) => {
    const response = await axios.post(`${API_URL}/leads`, data);
    return response.data;
  },

  updateLead: async (id, data) => {
    const response = await axios.put(`${API_URL}/leads/${id}`, data);
    return response.data;
  },

  deleteLead: async (id) => {
    const response = await axios.delete(`${API_URL}/leads/${id}`);
    return response.data;
  },

  getStats: async () => {
    // Add cache buster to prevent 304 responses
    const response = await axios.get(`${API_URL}/stats`, {
      params: { _t: Date.now() },
    });
    return response.data;
  },

  // ============================================================
  // MICROSOFT BOOKINGS
  // ============================================================
  getMicrosoftBookingServices: async (bookingAppId) => {
    const response = await axios.get(
      `${API_URL}/microsoft-bookings/services`,
      {
        params: { bookingAppId },
      },
    );
    return response.data;
  },

  getMicrosoftBookingAvailability: async (date, serviceId, bookingAppId) => {
    const response = await axios.get(
      `${API_URL}/microsoft-bookings/availability`,
      {
        params: { date, serviceId, bookingAppId },
      },
    );
    return response.data;
  },

  bookMicrosoftDemo: async (data) => {
    const response = await axios.post(
      `${API_URL}/microsoft-bookings/book`,
      data,
    );
    return response.data;
  },

  getMicrosoftAppointment: async (appointmentId) => {
    const response = await axios.get(
      `${API_URL}/microsoft-bookings/appointment/${appointmentId}`,
    );
    return response.data;
  },

  cancelMicrosoftDemo: async (leadId, message = "") => {
    const response = await axios.post(
      `${API_URL}/microsoft-bookings/cancel/${leadId}`,
      { message },
    );
    return response.data;
  },

  bookCalendlyDemo: async (leadId, date, time, notes = "", calendlyEventId = null) => {
    const response = await axios.post(`${API_URL}/demo/calendly`, {
      leadId,
      date,
      time,
      notes,
      calendlyEventId,
    });
    return response.data;
  },
  // 👇 NEW — used by BookDemo page for both Microsoft and Calendly
  bookUnifiedDemo: async (payload) => {
    const response = await axios.post(`${API_URL}/demo/unified`, payload);
    return response.data;
  },
  // ============================================================
  // BOOKING APPS
  // ============================================================
  getBookingApps: async () => {
    const response = await axios.get(`${API_URL}/booking-apps`);
    return response.data;
  },

  getAdminBookingApps: async () => {
    const response = await axios.get(`${API_URL}/booking-apps/admin`);
    return response.data;
  },

  createBookingApp: async (data) => {
    const response = await axios.post(`${API_URL}/booking-apps`, data);
    return response.data;
  },

  updateBookingApp: async (id, data) => {
    const response = await axios.put(`${API_URL}/booking-apps/${id}`, data);
    return response.data;
  },

  deleteBookingApp: async (id) => {
    const response = await axios.delete(`${API_URL}/booking-apps/${id}`);
    return response.data;
  },

  // ============================================================
  // ACTIVITIES
  // ============================================================
  addActivity: async (data) => {
    const response = await axios.post(`${API_URL}/activities`, data);
    return response.data;
  },

  getActivities: async (leadId) => {
    const response = await axios.get(`${API_URL}/leads/${leadId}/activities`);
    return response.data;
  },

  // ============================================================
  // MASTER DATA
  // ============================================================
  getMasterData: async (type) => {
    const response = await axios.get(`${API_URL}/master/${type}`);
    return response.data;
  },

  createMasterData: async (type, data) => {
    const response = await axios.post(`${API_URL}/master/${type}`, data);
    return response.data;
  },

  updateMasterData: async (type, id, data) => {
    const response = await axios.put(`${API_URL}/master/${type}/${id}`, data);
    return response.data;
  },

  deleteMasterData: async (type, id) => {
    const response = await axios.delete(`${API_URL}/master/${type}/${id}`);
    return response.data;
  },

  // ============================================================
  // ADMIN - USERS
  // ============================================================
  getUsers: async () => {
    const response = await axios.get(`${API_URL}/admin/users`);
    return response.data;
  },

  createUser: async (data) => {
    const response = await axios.post(`${API_URL}/admin/users`, data);
    return response.data;
  },

  updateUser: async (id, data) => {
    const response = await axios.put(`${API_URL}/admin/users/${id}`, data);
    return response.data;
  },

  deleteUser: async (id) => {
    const response = await axios.delete(`${API_URL}/admin/users/${id}`);
    return response.data;
  },

  // ============================================================
  // DOCUMENTS
  // ============================================================
  uploadDocuments: async (leadId, formData, onProgress) => {
    const response = await axios.post(
      `${API_URL}/leads/${leadId}/documents`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: onProgress,
      },
    );
    return response.data;
  },

  getDocuments: async (leadId) => {
    const response = await axios.get(`${API_URL}/leads/${leadId}/documents`);
    return response.data;
  },

  deleteDocument: async (leadId, documentId) => {
    const response = await axios.delete(
      `${API_URL}/leads/${leadId}/documents/${documentId}`,
    );
    return response.data;
  },

  getDocumentUrl: async (leadId, documentId) => {
    const response = await axios.get(
      `${API_URL}/leads/${leadId}/documents/${documentId}/url`,
    );
    return response.data;
  },

  downloadDocument: async (leadId, documentId) => {
    const response = await axios.get(
      `${API_URL}/leads/${leadId}/documents/${documentId}/download`,
      {
        responseType: "blob",
      },
    );
    return {
      blob: response.data,
      contentDisposition: response.headers["content-disposition"] || "",
    };
  },

  replaceDocument: async (leadId, documentId, formData, onProgress) => {
    const response = await axios.put(
      `${API_URL}/leads/${leadId}/documents/${documentId}`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: onProgress,
      },
    );
    return response.data;
  },

  // ============================================================
  // BULK IMPORT
  // ============================================================
  getImportOptions: async () => {
    const response = await axios.get(`${API_URL}/leads/import/options`);
    return response.data;
  },

  validateBulkImportLeads: async (payload) => {
    const response = await axios.post(
      `${API_URL}/leads/bulk-import/validate`,
      payload,
    );
    return response.data;
  },
  validateImportAgents: async (agentNames) => {
    const response = await axios.post(
      `${API_URL}/leads/import/validate-agents`,
      { agentNames },
    );
    return response.data;
  },

  createImportUser: async (data) => {
    const response = await axios.post(
      `${API_URL}/leads/import/create-user`,
      data,
    );
    return response.data;
  },

  bulkImportLeads: async (payload) => {
    const body = Array.isArray(payload) ? { leads: payload } : payload;
    const response = await axios.post(`${API_URL}/leads/bulk-import`, body);
    return response.data;
  },

  getImportStatus: async (importId) => {
    const response = await axios.get(
      `${API_URL}/leads/import/${importId}/status`,
    );
    return response.data;
  },

  getImportTemplate: async () => {
    const response = await axios.get(`${API_URL}/leads/import/template`);
    return response.data;
  },

  /* ============================================================
   OPTIMIZED DASHBOARD APIs
   These are separate from the existing getStats/getLeads APIs.
   ============================================================ */

  getDashboardStateCounts: async () => {
    const response = await axios.get(`${API_URL}/dashboard/state-counts`);
    return response.data;
  },

  getDashboardRecentActivities: async () => {
    const response = await axios.get(`${API_URL}/dashboard/recent-activities`);

    return response.data;
  },

  getDashboardUpcomingDemos: async () => {
    const response = await axios.get(`${API_URL}/dashboard/upcoming-demos`);

    return response.data;
  },
  getPaginatedLeads: async (filters = {}, page = 1, limit = 30) => {
    const params = {
      page,
      limit,
    };

    if (filters.status && filters.status !== "all") {
      params.status = filters.status;
    }

    if (filters.search) {
      params.search = filters.search;
    }

    if (filters.category) {
      params.category = filters.category;
    }

    if (filters.subcategory) {
      params.subcategory = filters.subcategory;
    }

    if (Array.isArray(filters.products) && filters.products.length > 0) {
      params.products = filters.products;
    }

    if (
      Array.isArray(filters.dmsProviders) &&
      filters.dmsProviders.length > 0
    ) {
      params.dmsProviders = filters.dmsProviders;
    }

    if (filters.leadType && filters.leadType !== "all") {
      params.leadType = filters.leadType;
    }

    if (filters.leadSource && filters.leadSource !== "all") {
      params.leadSource = filters.leadSource;
    }

    if (filters.serviceDashboard && filters.serviceDashboard !== "all") {
      params.serviceDashboard = filters.serviceDashboard;
    }

    if (filters.salesRep && filters.salesRep !== "all") {
      params.salesRep = filters.salesRep;
    }
    
    if (filters.nextContact && filters.nextContact !== "all") {
      params.nextContact = filters.nextContact;
    }
    const response = await axios.get(`${API_URL}/leads/paginated`, {
      params,
    });

    return response.data;
  },

  getLeadStateCounts: async (filters = {}) => {
    const params = {};

    if (filters.status && filters.status !== "all") {
      params.status = filters.status;
    }

    if (filters.search) {
      params.search = filters.search;
    }

    if (filters.category) {
      params.category = filters.category;
    }

    if (filters.subcategory) {
      params.subcategory = filters.subcategory;
    }

    if (Array.isArray(filters.products) && filters.products.length > 0) {
      params.products = filters.products;
    }

    if (
      Array.isArray(filters.dmsProviders) &&
      filters.dmsProviders.length > 0
    ) {
      params.dmsProviders = filters.dmsProviders;
    }

    if (filters.leadType && filters.leadType !== "all") {
      params.leadType = filters.leadType;
    }

    if (filters.leadSource && filters.leadSource !== "all") {
      params.leadSource = filters.leadSource;
    }

    if (filters.serviceDashboard && filters.serviceDashboard !== "all") {
      params.serviceDashboard = filters.serviceDashboard;
    }

    if (filters.salesRep && filters.salesRep !== "all") {
      params.salesRep = filters.salesRep;
    }

    const response = await axios.get(`${API_URL}/leads/state-counts`, {
      params,
    });

    return response.data;
  },

  exportLeads: async (filters = {}) => {
    const params = {};

    if (filters.status && filters.status !== "all") {
      params.status = filters.status;
    }

    if (filters.search && filters.search.trim()) {
      params.search = filters.search.trim();
    }

    if (filters.leadType && filters.leadType !== "all") {
      params.leadType = filters.leadType;
    }

    if (filters.leadSource && filters.leadSource !== "all") {
      params.leadSource = filters.leadSource;
    }

    if (filters.serviceDashboard && filters.serviceDashboard !== "all") {
      params.serviceDashboard = filters.serviceDashboard;
    }

    if (filters.salesRep && filters.salesRep !== "all") {
      params.salesRep = filters.salesRep;
    }

    if (Array.isArray(filters.dmsProviders) && filters.dmsProviders.length) {
      params.dmsProviders = filters.dmsProviders;
    }

    if (Array.isArray(filters.products) && filters.products.length) {
      params.products = filters.products;
    }

    const response = await axios.get(`${API_URL}/leads/export`, { params });

    return response.data;
  },
  
    
  /*
   * System-safe update — only the fields the admin is allowed to
   * change on a system-managed row (and also valid for non-system
   * rows).
   *
   * payload: { hideFromUser?: boolean, displayOrder?: number }
   */
  systemSafeUpdateMasterData: async (type, id, payload) => {
    const response = await axios.patch(
      `${API_URL}/master/${type}/${id}/system-safe`,
      payload,
    );
    return response.data;
  },

  /* ============================================================
     NEXT ACTIVITIES — additive methods
     ============================================================ */

  getNextActivitiesForLead: async (leadId) => {
    const response = await axios.get(
      `${API_URL}/leads/${leadId}/next-activities`,
    );
    return response.data;
  },

  getActiveNextActivity: async (leadId) => {
    const response = await axios.get(
      `${API_URL}/leads/${leadId}/next-activities/active`,
    );
    return response.data;
  },

  createNextActivity: async (leadId, payload) => {
    const response = await axios.post(
      `${API_URL}/leads/${leadId}/next-activities`,
      payload,
    );
    return response.data;
  },

  completeNextActivity: async (id, payload = {}) => {
    const response = await axios.patch(
      `${API_URL}/next-activities/${id}/complete`,
      payload,
    );
    return response.data;
  },

  suspendNextActivity: async (id, payload = {}) => {
    const response = await axios.patch(
      `${API_URL}/next-activities/${id}/suspend`,
      payload,
    );
    return response.data;
  },

  reopenNextActivity: async (id) => {
    const response = await axios.patch(
      `${API_URL}/next-activities/${id}/reopen`,
    );
    return response.data;
  },

  logNextActivityActivity: async (id, payload = {}) => {
    const response = await axios.patch(
      `${API_URL}/next-activities/${id}/log-activity`,
      payload,
    );
    return response.data;
  },

  getPastDueNextActivities: async () => {
    const response = await axios.get(`${API_URL}/next-activities/past-due`);
    return response.data;
  },

  bulkCompleteNextActivities: async (payload) => {
    const response = await axios.post(
      `${API_URL}/next-activities/bulk/complete`,
      payload,
    );
    return response.data;
  },

  bulkSuspendNextActivities: async (payload) => {
    const response = await axios.post(
      `${API_URL}/next-activities/bulk/suspend`,
      payload,
    );
    return response.data;
  },

  /* ============================================================
     NOTIFICATIONS — additive methods
     ============================================================ */

  getNotifications: async ({ unreadOnly = false, limit = 50 } = {}) => {
    const response = await axios.get(`${API_URL}/notifications`, {
      params: {
        unreadOnly: unreadOnly ? 1 : undefined,
        limit,
      },
    });
    return response.data;
  },

  getNotificationsUnreadCount: async () => {
    const response = await axios.get(
      `${API_URL}/notifications/unread-count`,
    );
    return response.data;
  },

  markNotificationRead: async (id) => {
    const response = await axios.patch(
      `${API_URL}/notifications/${id}/read`,
    );
    return response.data;
  },

  markAllNotificationsRead: async () => {
    const response = await axios.post(
      `${API_URL}/notifications/mark-all-read`,
    );
    return response.data;
  },

  /* ============================================================
     ADMIN — Notification settings
     ============================================================ */

  getAdminNotificationSettings: async () => {
    const response = await axios.get(
      `${API_URL}/admin/notification-settings`,
    );
    return response.data;
  },

  updateAdminNotificationSettings: async (payload) => {
    const response = await axios.put(
      `${API_URL}/admin/notification-settings`,
      payload,
    );
    return response.data;
  },

  /* ============================================================
     DASHBOARD — Past-due Next Activities widget
     ============================================================ */

  getDashboardPastDueNextActivities: async () => {
    const response = await axios.get(
      `${API_URL}/dashboard/past-due-next-activities`,
    );
    return response.data;
  },

  /* ============================================================
     NOTIFICATIONS — paginated / counts / team summary / bulk read
     ============================================================ */

  getNotificationsPaginated: async ({
    page = 1,
    limit = 50,
    isRead,
    type,
    since,
    scope = "mine",
  } = {}) => {
    const params = { page, limit, scope };
    if (isRead === true) params.isRead = 1;
    else if (isRead === false) params.isRead = 0;
    if (type && type !== "all") params.type = type;
    if (since) params.since = since;

    const response = await axios.get(`${API_URL}/notifications/paginated`, {
      params,
    });
    return response.data;
  },

  getNotificationCounts: async ({ scope = "mine" } = {}) => {
    const response = await axios.get(`${API_URL}/notifications/counts`, {
      params: { scope },
    });
    return response.data;
  },

  getNotificationTeamSummary: async () => {
    const response = await axios.get(
      `${API_URL}/notifications/team-summary`,
    );
    return response.data;
  },

  markManyNotificationsRead: async (ids) => {
    const response = await axios.post(
      `${API_URL}/notifications/mark-many-read`,
      { ids },
    );
    return response.data;
  },

  /* ============================================================
     ADMIN — Notification health
     ============================================================ */

  getAdminNotificationHealth: async () => {
    const response = await axios.get(
      `${API_URL}/admin/notification-health`,
    );
    return response.data;
  },

  sendTestNotificationEmail: async ({ subject, body }) => {
    const response = await axios.post(
      `${API_URL}/admin/notification-settings/send-test`,
      { subject, body },
    );
    return response.data;
  },  /* ============================================================
     R1 — Profile, Registration Approval, System Health
     ============================================================ */

  updateProfile: async (payload) => {
    const response = await axios.put(`${API_URL}/profile`, payload);
    return response.data;
  },

  changePassword: async (payload) => {
    const response = await axios.put(`${API_URL}/profile/password`, payload);
    return response.data;
  },

  updateNotificationPrefs: async (prefs) => {
    const response = await axios.put(`${API_URL}/profile/notification-prefs`, { prefs });
    return response.data;
  },

  uploadProfileImage: async (formData) => {
    const response = await axios.post(`${API_URL}/profile/image`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },

  listPendingUsers: async () => {
    const response = await axios.get(`${API_URL}/admin/pending-users`);
    return response.data;
  },

  countPendingUsers: async () => {
    const response = await axios.get(`${API_URL}/admin/pending-users/count`);
    return response.data;
  },

  approveUser: async (userId, payload) => {
    const response = await axios.post(`${API_URL}/admin/pending-users/${userId}/approve`, payload);
    return response.data;
  },

  rejectUser: async (userId) => {
    const response = await axios.post(`${API_URL}/admin/pending-users/${userId}/reject`);
    return response.data;
  },

  getSystemHealth: async () => {
    const response = await axios.get(`${API_URL}/admin/health/detailed`);
    return response.data;
  },
    /* R5-A — user deactivation + reactivation */
  userDeactivationPreview: async (userId) => {
    const response = await axios.get(
      `${API_URL}/admin/users/${userId}/deactivation-preview`,
    );
    return response.data;
  },

  deactivateUser: async (userId, reassignTo) => {
    const response = await axios.delete(
      `${API_URL}/admin/users/${userId}`,
      { data: { reassignTo } },
    );
    return response.data;
  },

  reactivateUser: async (userId) => {
    const response = await axios.post(
      `${API_URL}/admin/users/${userId}/reactivate`,
    );
    return response.data;
  },
  rotateRefreshToken: async (refreshToken) => {
    const response = await axios.post(`${API_URL}/auth/refresh/rotate`, { refreshToken });
    return response.data;
  },
    /* ============================================================
     R2 — Source Scope
     ============================================================ */

  getSourceScope: async (userId) => {
    const response = await axios.get(
      `${API_URL}/admin/users/${userId}/source-scope`,
    );
    return response.data;
  },

  setSourceScope: async (userId, payload) => {
    const response = await axios.put(
      `${API_URL}/admin/users/${userId}/source-scope`,
      payload,
    );
    return response.data;
  },

  getSourceScopePreview: async (scopeArray) => {
    const scope = Array.isArray(scopeArray) ? scopeArray.join(",") : "";
    const url = scope
      ? `${API_URL}/admin/users/0/source-scope/preview?scope=${encodeURIComponent(scope)}`
      : `${API_URL}/admin/users/0/source-scope/preview`;
    const response = await axios.get(url);
    return response.data;
  },

  getSourceScopeAudit: async (userId) => {
    const response = await axios.get(
      `${API_URL}/admin/users/${userId}/source-scope/audit`,
    );
    return response.data;
  },
  /* ============================================================
   R6 — Recent Lead Edits, Saved Views, Preferences,
         Scheduled Exports, Analytics
   ============================================================ */

// ----- Recent Lead Edits -----
recordRecentLeadEdit: async (payload) => {
  const response = await axios.post(`${API_URL}/r6/lead-edits`, payload);
  return response.data;
},
getRecentLeadEdits: async (leadId, limit = 25) => {
  const response = await axios.get(`${API_URL}/r6/leads/${leadId}/edits`, {
    params: { limit },
  });
  return response.data;
},

// ----- Saved Views -----
getSavedViews: async () => {
  const response = await axios.get(`${API_URL}/saved-views`);
  return response.data;
},
createSavedView: async (payload) => {
  const response = await axios.post(`${API_URL}/saved-views`, payload);
  return response.data;
},
updateSavedView: async (id, payload) => {
  const response = await axios.put(`${API_URL}/saved-views/${id}`, payload);
  return response.data;
},
deleteSavedView: async (id) => {
  const response = await axios.delete(`${API_URL}/saved-views/${id}`);
  return response.data;
},

// ----- User Preferences -----
getUserPreferences: async () => {
  const response = await axios.get(`${API_URL}/user-preferences`);
  return response.data;
},
getUserPreference: async (key) => {
  const response = await axios.get(
    `${API_URL}/user-preferences/${encodeURIComponent(key)}`,
  );
  return response.data;
},
setUserPreference: async (key, value) => {
  const response = await axios.put(
    `${API_URL}/user-preferences/${encodeURIComponent(key)}`,
    { value },
  );
  return response.data;
},

// ----- Scheduled Exports -----
getScheduledExports: async () => {
  const response = await axios.get(`${API_URL}/scheduled-exports`);
  return response.data;
},
getScheduledExport: async (id) => {
  const response = await axios.get(`${API_URL}/scheduled-exports/${id}`);
  return response.data;
},
createScheduledExport: async (payload) => {
  const response = await axios.post(`${API_URL}/scheduled-exports`, payload);
  return response.data;
},
updateScheduledExport: async (id, payload) => {
  const response = await axios.put(`${API_URL}/scheduled-exports/${id}`, payload);
  return response.data;
},
holdScheduledExport: async (id) => {
  const response = await axios.patch(`${API_URL}/scheduled-exports/${id}/hold`);
  return response.data;
},
resumeScheduledExport: async (id) => {
  const response = await axios.patch(`${API_URL}/scheduled-exports/${id}/resume`);
  return response.data;
},
delayScheduledExport: async (id, days) => {
  const response = await axios.patch(
    `${API_URL}/scheduled-exports/${id}/delay`,
    { days },
  );
  return response.data;
},
cancelScheduledExport: async (id) => {
  const response = await axios.patch(`${API_URL}/scheduled-exports/${id}/cancel`);
  return response.data;
},
runScheduledExportNow: async (id) => {
  const response = await axios.post(`${API_URL}/scheduled-exports/${id}/run-now`);
  return response.data;
},
getScheduledExportRuns: async (id) => {
  const response = await axios.get(`${API_URL}/scheduled-exports/${id}/runs`);
  return response.data;
},
// ----- Reports: run-now, email-now, preview -----
downloadScheduledExport: async (id) => {
  const response = await axios.post(
    `${API_URL}/scheduled-exports/${id}/run-download`,
    {},
    { responseType: "blob" },
  );
  return {
    blob: response.data,
    contentDisposition: response.headers["content-disposition"] || "",
  };
},

emailScheduledExport: async (id) => {
  const response = await axios.post(
    `${API_URL}/scheduled-exports/${id}/run-email`,
  );
  return response.data;
},

previewReport: async (payload) => {
  const response = await axios.post(
    `${API_URL}/scheduled-exports/preview`,
    payload,
  );
  return response.data;
},
// ----- Analytics -----
getAnalyticsRepLeaderboard: async (params = {}) => {
  const response = await axios.get(`${API_URL}/analytics/rep-leaderboard`, { params });
  return response.data;
},
getAnalyticsSourcePerformance: async (params = {}) => {
  const response = await axios.get(`${API_URL}/analytics/source-performance`, { params });
  return response.data;
},
getAnalyticsReminderResponsiveness: async (params = {}) => {
  const response = await axios.get(`${API_URL}/analytics/reminder-responsiveness`, { params });
  return response.data;
},
getAnalyticsStaleLeadsHeatmap: async (params = {}) => {
  const response = await axios.get(`${API_URL}/analytics/stale-leads-heatmap`, { params });
  return response.data;
},
getAnalyticsOverdueHeatmap: async (params = {}) => {
  const response = await axios.get(`${API_URL}/analytics/overdue-heatmap`, { params });
  return response.data;
},
getAnalyticsFilters: async () => {
  const response = await axios.get(`${API_URL}/analytics/filters`);
  return response.data;
},
};
export default api;